/* eslint-disable unicorn/no-document-cookie */
import { useState } from 'react';
import { sha256 } from 'js-sha256';
import { find, findIndex } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import { FRAuth, FRDevice, SessionManager, TokenManager, UserManager } from '@forgerock/javascript-sdk';
import { avOrganizationsApi, avUserApi } from '@availity/api-axios';

import envVar, { getSpecificEnv } from '@availity/env-var';
import { useEffectAsync } from '@availity/hooks';

import { getOaaOrgs, getInviteListCloud } from '@/api';
import { useAppContext } from '@/context';
import {
  JOURNEY_EMAIL_VERIFICATION,
  JOURNEY_FORGOTTEN_PASSWORD,
  JOURNEY_LOGIN_AVSIMPLE,
  COOKIE_NAME,
  JOURNEY_LOGIN,
  parseErrorCode,
  handleSuccessNavigation,
  JOURNEY_UPDATE_PASSWORD,
  JOURNEY_2FA_MANAGE,
  JOURNEY_SECURITY_QUESTIONS,
  createNRAttribute,
  JOURNEY_REGISTER,
  trackEvent,
} from '@/global';

const specificEnv = getSpecificEnv();
const nonProdUrlPrefix = `${specificEnv}-`;
const { host = '' } = window.location;
const lowercaseHost = host?.toLowerCase();
const hasDotNet = lowercaseHost.indexOf('.net') >= 0;

const logoutUrl = `https://${(specificEnv !== 'prod' && nonProdUrlPrefix) || ''}apps.availity${
  hasDotNet ? '.net' : '.com'
}/web/onboarding/availity-fr-ui/#/logout`;

const myAccountJourneyIds = [JOURNEY_UPDATE_PASSWORD, JOURNEY_2FA_MANAGE, JOURNEY_SECURITY_QUESTIONS];

const nonDismissibleErrorCodes = ['111'];

export const getOAuth = async (setUser) => {
  try {
    await TokenManager.getTokens({ forceRenew: true });
  } catch (error) {
    console.log(error);
  }

  try {
    const user = await UserManager.getCurrentUser();
    setUser(user);
  } catch (error) {
    if (error) {
      setUser({});
    }
  }
};

export default function useFRStep(previousStep, journeyId, forceAuth = false) {
  const [user] = useState();
  const {
    setLoading,
    setMessages,
    addMessage,
    queryParams,
    setQueryParams,
    setOaaOrgsResponse,
    setSelectedOAAOrgIds,
    setUserHasInvites,
  } = useAppContext();
  const navigate = useNavigate();
  const [stepToRender, setStepToRender] = useState();
  const [errorCode, setErrorCode] = useState();
  const location = useLocation();

  const {
    arg,
    authIndexValue,
    suspendedId,
    goto,
    spMetaAlias,
    idpEntityId,
    responseKey,
    responsekey,
    error: errorParam,
  } = queryParams;

  const lowercasedAuthIndexValue = authIndexValue?.toLowerCase();

  // NOTE: no need for unaffiliated user check if coming from my account - since we want the user to land back on my account app at the end of the journey; only necessary for login scenarios.
  let shouldSkipUnaffiliatedUserCheck = myAccountJourneyIds.indexOf(journeyId) >= 0;

  useEffectAsync(async () => {
    setLoading(true);

    // first, need to handle clearing of current session if arg=newsession param is passed
    if (arg?.toLowerCase() === 'newsession') {
      await SessionManager.logout();
    }

    let nextStep;
    try {
      if (
        suspendedId &&
        [
          JOURNEY_EMAIL_VERIFICATION,
          JOURNEY_FORGOTTEN_PASSWORD,
          JOURNEY_LOGIN_AVSIMPLE,
          JOURNEY_LOGIN,
          JOURNEY_REGISTER,
        ].indexOf(journeyId) >= 0
      ) {
        // for handling suspend nodes (i.e. from emails)
        nextStep = await FRAuth.next(null, { query: { ForceAuth: forceAuth, suspendedId }, tree: journeyId });
        setQueryParams({ ...queryParams, suspendedId: null });
      } else if (
        lowercasedAuthIndexValue?.indexOf('avsamlinbound') >= 0 &&
        spMetaAlias &&
        idpEntityId &&
        (responseKey || responsekey)
      ) {
        // for handling SAML inbound call
        nextStep = await FRAuth.next(previousStep, {
          query: {
            ForceAuth: forceAuth,
            spMetaAlias,
            idpEntityId,
            responsekey,
            responseKey,
            error: errorParam,
          },
          tree: journeyId,
        });
      } else {
        // normal authenticate call
        nextStep = await FRAuth.next(previousStep, { query: { ForceAuth: forceAuth }, tree: journeyId });
      }
    } catch (error) {
      if (error) {
        nextStep = {
          type: 'LoginFailure',
          payload: {
            message: 'Unknown request failure', // TODO: replace with our own error messages
          },
        };
      }
    }

    // first check for device profile callback, and handle device collection and set the device profile if needed for the nextStep
    const { payload = {}, type } = nextStep;
    const { callbacks = [] } = payload;
    const hasDeviceProfileCallback =
      findIndex(callbacks, (callbackObj) => callbackObj?.type?.toLowerCase() === 'deviceprofilecallback') >= 0;

    if (hasDeviceProfileCallback) {
      const deviceCollectorCallback = nextStep.getCallbackOfType('DeviceProfileCallback');
      const isLocationRequired = deviceCollectorCallback.isLocationRequired();
      const isMetadataRequired = deviceCollectorCallback.isMetadataRequired();

      const device = new FRDevice();
      const profile = await device.getProfile({
        location: isLocationRequired,
        metadata: isMetadataRequired,
      });

      nextStep.getCallbackOfType('DeviceProfileCallback').setProfile(profile);

      try {
        const updatedStep = { ...nextStep };

        nextStep = await FRAuth.next(updatedStep, { tree: journeyId });
      } catch (error) {
        if (error) {
          nextStep = {
            type: 'LoginFailure',
            payload: {
              message: 'Unknown request failure', // TODO: replace with our own error messages
            },
          };
        }
      }
    }

    // User is authenticated, now call for OAuth tokens
    if (type === 'LoginSuccess' || nextStep?.payload?.tokenId) {
      // cookie handling logic
      // set the session cookie at the 'availity.com' domain
      document.cookie = `${COOKIE_NAME}=${nextStep?.payload?.tokenId} ; path=/; domain=availity.com;`;

      // set the xsrf-token cookie at '.availity.com' domain to account for both apps and essentials domains
      const tokenHash = sha256(nextStep?.payload?.tokenId);
      document.cookie = `XSRF-TOKEN=${tokenHash}; Secure; path=/; domain=.availity.com;`;

      // log success in new relic
      createNRAttribute(location.pathname, nextStep);

      // handle re-mounting of HUMAN script post-successful login
      const humanScript = document.createElement('script');
      humanScript.src =
        specificEnv === 'prod'
          ? '//client.px-cloud.net/PX2yes24ut/main.min.js'
          : '//client.px-cloud.net/PXbE3Rqbur/main.min.js';
      humanScript.async = true;

      document.body.appendChild(humanScript);

      // handle getting the akaName and setting it in a 'sessionAka' cookie for HUMAN to grab.
      // also check if the user should see the new nav bar
      let navVersion = '1';

      try {
        const meResponse = await avUserApi.me({
          params: {
            includeNavVersion: true,
          },
        });

        const { akaname, navVersion: navCode = '1' } = meResponse;

        if (akaname) {
          document.cookie = `sessionAka=${akaname}; Secure; path=/; domain=.availity.com;`;
        }

        // set a cookie with the navVersion
        if (navCode) {
          const navCookieAttribute = envVar({
            prod: 'prod-navVersion',
            qa: 'qa-navVersion',
            local: 'test-navVersion',
          });

          document.cookie = `${navCookieAttribute}=${navCode}; Secure; path=/; domain=.availity.com;`;
        }

        navVersion = navCode;
      } catch (error) {
        if (error) {
          addMessage('Unable to get the logged in user info.');
        }
      }

      // handle inbound sso logic; check if successUrl contains 'magneto'; if so, override other login success logic
      let shouldUseSuccessUrl = false;
      if (nextStep?.payload?.successUrl?.indexOf('magneto') >= 0) {
        shouldSkipUnaffiliatedUserCheck = true;
        shouldUseSuccessUrl = true;
      }

      // check if the user has any open invitations. if so, need to ensure that navigation to the invitations UI happens after the login flow.
      let shouldGoToInvitationsUI = false;
      if ((journeyId === JOURNEY_LOGIN || goto?.indexOf('invite-list') >= 0) && !shouldUseSuccessUrl) {
        let invitationsResponse;
        try {
          invitationsResponse = await getInviteListCloud();

          const { pendingInvitations = [], expiredInvitations = [] } = invitationsResponse;

          if (pendingInvitations.length > 0 || expiredInvitations.length > 0) {
            setUserHasInvites(true); // set this in state for OAA or other processes to pull from
            shouldGoToInvitationsUI = true; // keep this local variable for navigations from this file
            shouldSkipUnaffiliatedUserCheck = true; // no need to check for affiliated orgs anymore
          }
        } catch (error) {
          if (error) {
            addMessage("Oops! Something went wrong when getting the logged in user's invitations.");
            trackEvent({
              event: 'get-invitations-request',
              status: error?.response?.status,
              api: 'user-org-management-api',
            });
          }
        }
      }

      // check if user needs to go through the unaffiliated user flow
      let onlyHasUserOrg = false;

      if (!shouldSkipUnaffiliatedUserCheck) {
        let orgsResponse;
        try {
          orgsResponse = await avOrganizationsApi.getOrganizations();
          const { data = {} } = orgsResponse;
          const { organizations = [] } = data;

          if (organizations?.length === 1) {
            const { types = [] } = organizations[0];
            const { code } = types[0];

            if (code === '40') {
              onlyHasUserOrg = true;
            }
          }
        } catch (error) {
          addMessage("Oops! Something went wrong when getting the logged in user's organizations.");
          trackEvent({
            event: 'get-organizations-request',
            status: error?.response?.status,
            api: 'organizations-api',
          });
        }
      }

      // check if user has admin role and has not signed latest OAA agreement; need to redirect to the OAA flow if the user needs to sign one.
      let isOaaPending = false;
      if (journeyId === JOURNEY_LOGIN && !onlyHasUserOrg) {
        try {
          const oaaOrgsResponse = await getOaaOrgs();
          if (oaaOrgsResponse) {
            const { oaaOrganizations, skipPage, totalOrgCount } = oaaOrgsResponse;

            if (!skipPage && totalOrgCount > 0) {
              setOaaOrgsResponse(oaaOrgsResponse);
              isOaaPending = true;

              // all organizations should be selected by default; initialize state
              const orgIds = oaaOrganizations.map((organization) => organization.appId);
              setSelectedOAAOrgIds([...orgIds]);
            }
          }
        } catch (error) {
          if (error) {
            addMessage(
              "Unable to retrieve the OAA for the logged in user's organizations. Reload the page and try again."
            );
          }
        }
      }

      setLoading(false);

      // if user only has org type 40, navigate to unaffiliated user flow; otherwise, handle the normal navigation.
      if (onlyHasUserOrg) {
        navigate('/next-steps');
      } else if (journeyId === JOURNEY_LOGIN && isOaaPending) {
        navigate('/oaa');
      } else {
        // for safety, need to check that the goto url domain matches the window domain

        // use the successUrl if indicated, otherwise use the 'goto' url.
        const gotoToUse = shouldUseSuccessUrl ? nextStep?.payload?.successUrl : goto;

        handleSuccessNavigation(gotoToUse, shouldGoToInvitationsUI, navVersion);
      }
    }

    if (type === 'LoginFailure') {
      const { payload = {} } = nextStep;
      const { message = '' } = payload;

      let reloadedStep;

      if (message && message === 'Session has timed out') {
        // for login scenario where the user loads the login journey and is idle for too long before getting the next step
        addMessage({
          type: 'info',
          text: 'Your login page was idle for too long. Please try again.',
          icon: 'icon-attention-circle',
          dismissible: true,
        });
      } else if (message && message === 'User Locked Out.') {
        // handle log out scenario - and pass in the error code through query params
        window.top.location.href = `${logoutUrl}?errorCode=111`;
      } else if (message && message !== '0') {
        // grab the error message if available; note that if undefined, the UI will default to a generic oops message.
        setErrorCode(message);
        const errorToDisplay = parseErrorCode(message, setMessages, journeyId);
        addMessage(errorToDisplay || 'Oops! Something went wrong. Reload the page and try again.');

        // log specific failure in new relic
        createNRAttribute(location.pathname, nextStep, message);
      } else {
        // clear out error code if not message or if message code is 0
        setErrorCode();

        // log generic failure in new relic
        createNRAttribute(location.pathname, nextStep, '999');
      }

      // if reloadedStep is not yet assigned, get next step without previous step to get the new authId
      if (!reloadedStep) {
        try {
          reloadedStep = await FRAuth.next(null, { tree: journeyId });
        } catch (error) {
          if (error) {
            reloadedStep = {
              type: 'LoginFailure',
              payload: {
                message: 'Unknown request failure', // TODO: replace with our own error messages
              },
            };

            addMessage('Oops! Something went wrong. Reload the page and try again.');
          }
        }
      }

      // repopulate callbacks/payload with previous data
      if (reloadedStep?.payload?.stage === previousStep?.payload?.stage) {
        reloadedStep.callbacks = previousStep?.callbacks;
        reloadedStep.payload = {
          ...previousStep?.payload,
          authId: reloadedStep?.payload?.authId,
        };

        // log reloaded step failure in new relic
        createNRAttribute(location.pathname, reloadedStep, '999');
      } else if (reloadedStep?.type === 'LoginFailure') {
        // if re-fetching FR step fails (likely due to connection failure), handle here.
        // need the user to manually restart the journey
        reloadedStep.payload = {
          ...reloadedStep?.payload,
          stage: 'ErrorPage',
        };

        // log generic failure in new relic
        createNRAttribute(location.pathname, reloadedStep, '999');
      }

      setLoading(false);

      setStepToRender(reloadedStep);
    } else {
      // in the event that a 200 with a 'retry' step is returned, need to check for the hidden value callback with a error message in case we need to display an error to the user.
      const errorValueCallback = find(
        callbacks,
        (callbackObj) =>
          callbackObj?.type?.toLowerCase() === 'hiddenvaluecallback' &&
          callbackObj?.input[0].value?.toLowerCase() === 'errorcode'
      );

      if (errorValueCallback) {
        const messageValueObj =
          find(errorValueCallback.output, (outputObj) => outputObj?.name?.toLowerCase() === 'value') || {};
        const { value: messageCode } = messageValueObj;

        if (messageCode && messageCode !== '0') {
          setErrorCode(messageCode);
          const message = parseErrorCode(messageCode, setMessages, journeyId);
          const messageToDisplay =
            typeof message === 'string' ? { icon: 'icon-attention-circle', text: message } : { ...message };

          // handle non-dismissible error codes
          if (nonDismissibleErrorCodes.indexOf(messageCode) >= 0) {
            messageToDisplay.dismissible = false;
          }

          // log 'retry' failure in new relic
          createNRAttribute(location.pathname, nextStep, messageCode);

          addMessage(messageToDisplay);
        } else {
          // clear out error code and messages if none present or message is 0
          setErrorCode();
          setMessages([]);
        }
      }

      // log successful next step in new relic
      createNRAttribute(location.pathname, nextStep);

      setLoading(false);

      setStepToRender(nextStep);
    }

    return null;
  }, [previousStep, journeyId]);

  return { user, stepToRender, errorCode };
}
